// Saved chats + folders for the signed-in person at the current venue (private; RLS enforces it).
// Everything here is best-effort: if saving fails, the chat itself keeps working.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';
import type { Message } from '@/src/lib/types';

export interface ChatSummary { id: string; title: string; updated_at: string; folder_id: string | null; pinned: boolean }
export interface ChatFolder { id: string; name: string; created_at: string }

// Quick title used straight away (and as the fallback): strip greetings/filler, first line, ~50 chars.
// A short AI summary ("Pornstar Martini Recipe") replaces it a moment later.
export function titleFrom(text: string): string {
  let line = text.replace(/\s+/g, ' ').trim();
  line = line.replace(/^(hi|hey|hello|yo|hiya|good (morning|afternoon|evening))\b[\s,!.]*/i, '')
    .replace(/^(can|could|would) you (please )?/i, '').replace(/^please /i, '').trim();
  if (line) line = line[0].toUpperCase() + line.slice(1);
  if (line.length <= 50) return line || 'New chat';
  const cut = line.slice(0, 50);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 30 ? cut.lastIndexOf(' ') : 50)}…`;
}

const BASE_COLS = 'id, title, updated_at';
const FULL_COLS = 'id, title, updated_at, folder_id, pinned';

export function useChatHistory(venueId: string | null, userId: string | null) {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [folders, setFolders] = useState<ChatFolder[]>([]);
  const [available, setAvailable] = useState(true);              // false until the chat history SQL has been run
  const [foldersAvailable, setFoldersAvailable] = useState(true); // false until the chat folders SQL has been run

  const load = useCallback(async () => {
    if (!venueId || !userId) { setChats([]); setFolders([]); return; }
    let { data, error } = await supabase.from('chat_conversations').select(FULL_COLS)
      .eq('venue_id', venueId).eq('user_id', userId).order('updated_at', { ascending: false }).limit(200);
    let hasFolders = true;
    if (error && /folder_id|pinned/.test(error.message)) {
      hasFolders = false;
      const r2 = await supabase.from('chat_conversations').select(BASE_COLS)
        .eq('venue_id', venueId).eq('user_id', userId).order('updated_at', { ascending: false }).limit(200);
      data = r2.data as typeof data; error = r2.error;
    }
    if (error) { setAvailable(!/chat_conversations/.test(error.message)); log.warn('chat history load failed', { code: error.code }); return; }
    setAvailable(true);
    setChats(((data ?? []) as Partial<ChatSummary>[]).map((r) => ({ folder_id: null, pinned: false, ...r }) as ChatSummary));

    if (!hasFolders) { setFoldersAvailable(false); return; }
    const f = await supabase.from('chat_folders').select('id, name, created_at')
      .eq('venue_id', venueId).eq('user_id', userId).order('created_at', { ascending: true });
    if (f.error) { setFoldersAvailable(false); log.warn('chat folders load failed', { code: f.error.code }); return; }
    setFoldersAvailable(true);
    setFolders((f.data as ChatFolder[]) ?? []);
  }, [venueId, userId]);

  useEffect(() => { void load(); }, [load]);

  const rename = useCallback(async (conversationId: string, title: string) => {
    const t = title.trim().slice(0, 80) || 'New chat';
    setChats((p) => p.map((c) => (c.id === conversationId ? { ...c, title: t } : c)));
    await supabase.from('chat_conversations').update({ title: t }).eq('id', conversationId);
  }, []);

  const create = useCallback(async (firstMessage: string): Promise<string | null> => {
    if (!venueId || !userId || !available) return null;
    const quick = titleFrom(firstMessage);
    const { data, error } = await supabase.from('chat_conversations')
      .insert({ venue_id: venueId, user_id: userId, title: quick }).select(BASE_COLS).single();
    if (error || !data) { log.warn('chat create failed', { code: error?.code }); return null; }
    const row = { folder_id: null, pinned: false, ...(data as Omit<ChatSummary, 'folder_id' | 'pinned'>) };
    setChats((p) => [row, ...p]);
    // Short summary title in the background; only applied if the person hasn't renamed it meanwhile.
    void supabase.functions.invoke('chat-title', { body: { text: firstMessage } }).then(({ data: t }) => {
      const title = (t as { title?: string | null } | null)?.title?.trim();
      if (!title) return;
      setChats((p) => {
        const hit = p.find((c) => c.id === row.id);
        if (!hit || hit.title !== quick) return p;
        void supabase.from('chat_conversations').update({ title }).eq('id', row.id).eq('title', quick);
        return p.map((c) => (c.id === row.id ? { ...c, title } : c));
      });
    }).catch(() => {});
    return row.id;
  }, [venueId, userId, available]);

  const append = useCallback(async (conversationId: string, m: Pick<Message, 'role' | 'content' | 'toolEvents' | 'suggestions'>) => {
    if (!m.content?.trim()) return;
    const now = new Date().toISOString();
    const { error } = await supabase.from('chat_messages').insert({
      conversation_id: conversationId, role: m.role, content: m.content,
      tool_events: m.toolEvents?.length ? m.toolEvents : null, suggestions: m.suggestions?.length ? m.suggestions : null,
    });
    if (error) { log.warn('chat save failed', { code: error.code }); return; }
    await supabase.from('chat_conversations').update({ updated_at: now }).eq('id', conversationId);
    setChats((p) => {
      const hit = p.find((c) => c.id === conversationId);
      return hit ? [{ ...hit, updated_at: now }, ...p.filter((c) => c.id !== conversationId)] : p;
    });
  }, []);

  const open = useCallback(async (conversationId: string): Promise<Message[]> => {
    const { data, error } = await supabase.from('chat_messages').select('id, role, content, tool_events, suggestions, created_at')
      .eq('conversation_id', conversationId).order('created_at', { ascending: true }).limit(500);
    if (error) { log.warn('chat open failed', { code: error.code }); return []; }
    return ((data ?? []) as Array<{ id: string; role: 'user' | 'assistant'; content: string; tool_events: Message['toolEvents'] | null; suggestions: Message['suggestions'] | null; created_at: string }>)
      .map((r) => ({ id: r.id, role: r.role, content: r.content, timestamp: new Date(r.created_at), toolEvents: r.tool_events ?? undefined, suggestions: r.suggestions ?? undefined }));
  }, []);

  const remove = useCallback(async (conversationId: string) => {
    setChats((p) => p.filter((c) => c.id !== conversationId));
    const { error } = await supabase.from('chat_conversations').delete().eq('id', conversationId);
    if (error) { log.warn('chat delete failed', { code: error.code }); void load(); }
  }, [load]);

  const setPinned = useCallback(async (conversationId: string, pinned: boolean) => {
    setChats((p) => p.map((c) => (c.id === conversationId ? { ...c, pinned } : c)));
    const { error } = await supabase.from('chat_conversations').update({ pinned }).eq('id', conversationId);
    if (error) { log.warn('chat pin failed', { code: error.code }); void load(); }
  }, [load]);

  const moveToFolder = useCallback(async (conversationId: string, folderId: string | null) => {
    setChats((p) => p.map((c) => (c.id === conversationId ? { ...c, folder_id: folderId } : c)));
    const { error } = await supabase.from('chat_conversations').update({ folder_id: folderId }).eq('id', conversationId);
    if (error) { log.warn('chat move failed', { code: error.code }); void load(); }
  }, [load]);

  const createFolder = useCallback(async (name: string): Promise<string | null> => {
    if (!venueId || !userId) return null;
    const n = name.trim().slice(0, 60) || 'New folder';
    const { data, error } = await supabase.from('chat_folders').insert({ venue_id: venueId, user_id: userId, name: n })
      .select('id, name, created_at').single();
    if (error || !data) { log.warn('folder create failed', { code: error?.code }); return null; }
    setFolders((p) => [...p, data as ChatFolder]);
    return (data as ChatFolder).id;
  }, [venueId, userId]);

  const renameFolder = useCallback(async (folderId: string, name: string) => {
    const n = name.trim().slice(0, 60) || 'New folder';
    setFolders((p) => p.map((f) => (f.id === folderId ? { ...f, name: n } : f)));
    await supabase.from('chat_folders').update({ name: n }).eq('id', folderId);
  }, []);

  // Deleting a folder keeps its chats: they go back to Recents.
  const deleteFolder = useCallback(async (folderId: string) => {
    setFolders((p) => p.filter((f) => f.id !== folderId));
    setChats((p) => p.map((c) => (c.folder_id === folderId ? { ...c, folder_id: null } : c)));
    const { error } = await supabase.from('chat_folders').delete().eq('id', folderId);
    if (error) { log.warn('folder delete failed', { code: error.code }); void load(); }
  }, [load]);

  return {
    chats, folders, available, foldersAvailable,
    create, append, open, rename, remove, setPinned, moveToFolder,
    createFolder, renameFolder, deleteFolder, reload: load,
  };
}

export type ChatHistory = ReturnType<typeof useChatHistory>;
